import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { getNetworkInvoices, type MyNetworkInvoice } from '@/api/tenantParties';

export function OutstandingInvoicesDialog({
  tenantPartyId, partyName, onClose,
}: { tenantPartyId: number | null; partyName?: string; onClose: () => void }) {
  const [invoices, setInvoices] = useState<MyNetworkInvoice[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tenantPartyId == null) return;
    setLoading(true);
    getNetworkInvoices(tenantPartyId)
      .then(list => setInvoices(list.filter(i => (i.due ?? 0) > 0)))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [tenantPartyId]);

  if (tenantPartyId == null) return null;

  const total = invoices.reduce((s, i) => s + (i.due ?? 0), 0);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, pr: 6 }}>
        Outstanding Invoices{partyName ? ` — ${partyName}` : ''}
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>✕</IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : invoices.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No outstanding invoices.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 1, py: 0.6, borderBottom: '2px solid #e5e7eb' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Invoice #</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Date</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Amount</Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>Due</Typography>
            </Box>
            {invoices.map(inv => (
              <Box key={inv.id ?? inv.invoiceNumber} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 1, py: 0.6, borderBottom: '1px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: 12 }}>{inv.invoiceNumber}</Typography>
                <Typography sx={{ fontSize: 12 }}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '—'}</Typography>
                <Typography sx={{ fontSize: 12 }}>₹{(inv.invoiceAmount ?? 0).toLocaleString()}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>₹{(inv.due ?? 0).toLocaleString()}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Total Outstanding: ₹{total.toLocaleString()}</Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
